import { z } from "zod";

/** Repository roles within a project: one primary, the rest secondary. */
export const repositoryRoles = ["primary", "secondary"] as const;
export const repositoryRoleSchema = z.enum(repositoryRoles);
export type RepositoryRole = z.infer<typeof repositoryRoleSchema>;

/** Webhook delivery states, as the repositories table badges them. */
export const webhookStates = ["delivering", "failed", "not-verified"] as const;
export const webhookStateSchema = z.enum(webhookStates);
export type WebhookState = z.infer<typeof webhookStateSchema>;

/**
 * One bound repository, as `GET /api/v1/projects/:id/repositories` returns it. The credential
 * is a stored reference id, never a value, and no endpoint ever returns the value: the page
 * shows that a credential exists, never what it is.
 */
export const repositorySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  role: repositoryRoleSchema,
  defaultBranch: z.string().min(1),
  credentialRef: z.string().min(1),
  webhookState: webhookStateSchema,
  branchProtection: z.string().nullable(),
});
export type Repository = z.infer<typeof repositorySchema>;

export const repositoryListSchema = z.array(repositorySchema);
export type RepositoryList = z.infer<typeof repositoryListSchema>;

/**
 * `POST /api/v1/projects/:id/repositories`. Binds one repository. The credential travels only
 * as a reference id: a value in this body is rejected, so a run can never smuggle a write
 * credential through the bind.
 */
export const bindRepositorySchema = z.object({
  name: z.string().trim().min(1).max(200),
  role: repositoryRoleSchema,
  defaultBranch: z.string().trim().min(1).max(120).optional().default("main"),
  credentialRef: z.string().trim().min(1).max(120),
});
export type BindRepository = z.infer<typeof bindRepositorySchema>;

/** Pull request check states, as the pull request list badges them. */
export const pullRequestCheckStates = ["passed", "running", "failed", "not-requested"] as const;
export const pullRequestCheckStateSchema = z.enum(pullRequestCheckStates);
export type PullRequestCheckState = z.infer<typeof pullRequestCheckStateSchema>;

/**
 * One pull request runs have opened, as `GET /api/v1/projects/:id/pull-requests` returns it.
 * Merges go through a brokered, approved action: the write path requires an approval, never a
 * credential handed to the run.
 */
export const pullRequestSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  title: z.string().min(1),
  head: z.string().min(1),
  base: z.string().min(1),
  runId: z.uuid().nullable(),
  checks: pullRequestCheckStateSchema,
  checksDetail: z.string().nullable(),
  reviewState: z.string().min(1),
  mergeState: z.string().min(1),
  url: z.string().nullable(),
});
export type PullRequest = z.infer<typeof pullRequestSchema>;

export const pullRequestListSchema = z.array(pullRequestSchema);
export type PullRequestList = z.infer<typeof pullRequestListSchema>;
